import React, { useState } from 'react';
import { addDays, format, isSameDay, isSameYear } from 'date-fns';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';

export default function DatePickerHeader({ dateRange, onDateRangeChange }) {
  const [isOpen, setIsOpen] = useState(false);

  const formatRange = () => {
    if (!dateRange?.start && !dateRange?.end) return 'Alle Daten';
    if (!dateRange?.start) return 'Zeitraum waehlen';
    if (dateRange.start && !dateRange.end) return format(dateRange.start, 'dd. MMM');

    if (isSameDay(dateRange.start, dateRange.end)) {
      return format(dateRange.start, 'EEE, dd. MMM');
    }

    const startFormat = isSameYear(dateRange.start, dateRange.end) ? 'dd. MMM' : 'dd. MMM yyyy';
    return `${format(dateRange.start, startFormat)} - ${format(dateRange.end, 'dd. MMM')}`;
  };

  const shiftRange = (offsetDays) => {
    if (!dateRange?.start) return;
    const nextStart = addDays(dateRange.start, offsetDays);
    const nextEnd = dateRange?.end ? addDays(dateRange.end, offsetDays) : nextStart;
    onDateRangeChange({ from: nextStart, to: nextEnd });
  };

  return (
    <div className="flex items-center gap-0.5 rounded-md border bg-background p-0.5 shadow-sm">
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 hover:bg-muted"
        onClick={() => shiftRange(-1)}
        disabled={!dateRange?.start}
      >
        <ChevronLeft className="h-3.5 w-3.5" />
      </Button>

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'h-7 w-[150px] justify-start border-0 bg-transparent px-2 text-left text-xs font-normal hover:bg-muted sm:w-[190px]',
              !dateRange?.start && 'text-muted-foreground',
              isOpen && 'bg-muted',
            )}
          >
            <CalendarIcon className="mr-2 h-3.5 w-3.5 opacity-50" />
            <span className="truncate">{formatRange()}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            mode="range"
            defaultMonth={dateRange?.start}
            selected={dateRange ? { from: dateRange.start, to: dateRange.end } : undefined}
            onSelect={onDateRangeChange}
            numberOfMonths={2}
            initialFocus
          />
        </PopoverContent>
      </Popover>

      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 hover:bg-muted"
        onClick={() => shiftRange(1)}
        disabled={!dateRange?.start}
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
